import { joiResolver } from "@hookform/resolvers";
import { Box, Button, makeStyles, TextField } from "@material-ui/core";
import { px } from "Configs/theme";
import Joi from "joi";
import { useTranslation } from "Providers/TranslationProvider";
import React, { FC } from "react";
import { useForm } from "react-hook-form";
import { v4 } from "uuid";
import { useOrganizationSuggestionsStore } from "./OrganizationSuggestionStore";
import moment from "moment";
import { useViewer } from "Components/Auth/AuthProvider";
import { useSnackbar } from "material-ui-snackbar-provider";
import { TrackingEvent, useTracking } from "../../Providers/TrackingProvider";

interface FormValues {
  organizationName: string;
  organizationCity: string;
}

interface Props {
  close: () => void;
}

const OrganizationSuggestionForm: FC<Props> = (props) => {
  const { t } = useTranslation();
  const viewer = useViewer();
  const organizationSuggestionsStore = useOrganizationSuggestionsStore();
  const {trackEvent} = useTracking();
  const snackbar = useSnackbar();
  const classes = useStyles();

  const defaultValues: FormValues = {
    organizationName: "",
    organizationCity: "",
  };

  const schema = Joi.object({
    organizationName: Joi.string()
      .required()
      .messages({
        "string.empty": t("Common_Form_Error_MandatoryField", {
          field: t("OrganizationSuggestionForm_Label_OrganizationName"),
        }),
      }),
    organizationCity: Joi.string()
      .required()
      .messages({
        "string.empty": t("Common_Form_Error_MandatoryField", {
          field: t("OrganizationSuggestionForm_Label_OrganizationCity"),
        }),
      }),
  });

  const form = useForm<FormValues>({
    resolver: joiResolver(schema),
    defaultValues,
  });

  async function onSubmit(formValues: FormValues) {
    try {
      await organizationSuggestionsStore.setItem({
        id: v4(),
        createdTimestamp: moment().unix(),
        organizationCity: formValues.organizationCity,
        organizationName: formValues.organizationName,
        userId: viewer.user.uid,
      });

      snackbar.showMessage(
          t("OrganizationSuggestionForm_Snackbar_Success"),
          undefined,
          undefined,
          {
            alertSeverity: "success",
          }
      );
      trackEvent(TrackingEvent.NEW_PARTNER_REQUEST_SUCCESS);
      props.close();
    } catch (error) {
      snackbar.showMessage(error.message);
    }
  }

  const isDisabled = form.formState.isSubmitting;

  return (
    <form onSubmit={form.handleSubmit(onSubmit)}>
      <Box className={classes.title}>
        {t("OrganizationSuggestionForm_Title")}
      </Box>
      <Box className={classes.fieldBox}>
        <TextField
          name="organizationName"
          variant="outlined"
          label={t("OrganizationSuggestionForm_Label_OrganizationName")}
          inputRef={form.register}
          error={!!form.errors.organizationName}
          helperText={form.errors.organizationName?.message}
          disabled={isDisabled}
          autoFocus
          fullWidth
        />
      </Box>
      <Box className={classes.fieldBox}>
        <TextField
          name="organizationCity"
          variant="outlined"
          label={t("OrganizationSuggestionForm_Label_OrganizationCity")}
          inputRef={form.register}
          error={!!form.errors.organizationCity}
          helperText={form.errors.organizationCity?.message}
          disabled={isDisabled}
          fullWidth
        />
      </Box>
      <Box className={classes.submitBox}>
        <Button
          className={classes.submitButton}
          type="submit"
          color="primary"
          variant="contained"
          disabled={isDisabled}
          disableElevation
        >
          {t("OrganizationSuggestionForm_Button_Submit")}
        </Button>
      </Box>
    </form>
  );
};

export default OrganizationSuggestionForm;

const useStyles = makeStyles({
  title: {
    fontSize: "16px",
    textAlign: "center",
    fontWeight: 500,
    marginBottom: px.xxl,
  },
  fieldBox: {
    marginBottom: px.l,
  },
  submitBox: {
    display: "flex",
    justifyContent: "center",
    marginBottom: px.l,
  },
  submitButton: {
    fontSize: "14px",
    fontWeight: "bold",
  },
});
